// src/components/DpcTensuPanel.tsx
// DPC 点数パネル (DPC分析タブ最下部に統合)。 14 桁 DPC コード → 包括点数 (sheet 16) を可視化。
//
// 連動方針 (2026-05-28 統合):
//   - 上位表 (DPC drill-down) の選択 path から自動で 14 桁を解決:
//     selectedDpc14 > selectedDpc12 + "xx" > selectedDpc11 + "xxx"
//     > selectedDpc10 + "xxxx" > dpc6 + "xxxxxxxx"
//   - 加えてユーザが任意の dpc14 を手入力で override 可能 (= 残置仕様)
//   - 上位が全て未選択 → placeholder「上の表で選択してください」 表示
//
// 構成:
//   1. 入力部: 14 桁直接入力 (テキスト、 上位から auto-sync) + 医療機関別係数 + 想定入院日数 slider
//   2. 階段チャート (DpcStepChart)
//   3. 期間表 (期間 I/II/III × 境界日 / 1日点数 / 1日金額 / 累計金額)
//   4. メタ情報表 (傷病名 / 手術 / 処置1 / 処置2 / 副傷病 / 重症度 / 有効期間 / 区分)
//
// 入院期間 III 超 = 出来高算定 (注記)。
// 包括金額 = 1 日点数 × 入院日数 × 係数 × 10 円。
import { useState, useMemo, useEffect, useRef } from "react";
import { useDpcTensu } from "../lib/useDpcTensu";
import { useDpcCoef } from "../lib/useDpcCoef";
import { diseasePeriod } from "../lib/useDiseaseRanking";
import { useFilters } from "../store";
import { ymJp } from "../lib/format";
import { DpcStepChart } from "./DpcStepChart";

// 上位表の選択 path (最深) から 14 桁文字列を組み立てる。
// 14 桁未満なら末尾を "x" でパディング。 全 null → null。
function deriveDpc14FromSelection(
  selectedDpc14: string | null,
  selectedDpc12: string | null,
  selectedDpc11: string | null,
  selectedDpc10: string | null,
  dpc6: string | null,
): string | null {
  const base =
    selectedDpc14 ??
    selectedDpc12 ??
    selectedDpc11 ??
    selectedDpc10 ??
    dpc6 ??
    null;
  if (!base) return null;
  if (base.length >= 14) return base.slice(0, 14);
  return base + "x".repeat(14 - base.length);
}

export function DpcTensuPanel() {
  const f = useFilters();

  // 上位表の選択から自動で 14 桁を解決 (= autoSelected)
  const autoSelected = deriveDpc14FromSelection(
    f.selectedDpc14,
    f.selectedDpc12,
    f.selectedDpc11,
    f.selectedDpc10,
    f.dpc6,
  );

  // 手動 override (= ユーザが input で 任意 dpc14 を試す)。
  // null = 「auto に従う」。 文字列 = 「ユーザが override 中」。
  const [manualDpc14, setManualDpc14] = useState<string | null>(null);

  // auto が変わったら manual override を解除 (= 上位表で行クリックするたびに auto に追従)。
  // 初回 mount 時に autoSelected が null → そのまま (manual も null = 未入力扱い)。
  const prevAutoRef = useRef<string | null>(autoSelected);
  useEffect(() => {
    if (prevAutoRef.current !== autoSelected) {
      prevAutoRef.current = autoSelected;
      setManualDpc14(null);   // auto 変更を優先、 manual を破棄
    }
  }, [autoSelected]);

  // 最終的に fetch に使う 14 桁
  const dpc14 = manualDpc14 ?? autoSelected ?? "";

  // [DB係数 2026-07-17] 医療機関別係数は DB (dim_dpc_coef = 電カル masijinyusysf 月次) から
  // 自動解決。適用月 = 表示期間の終了月 (係数は請求月適用のため。2026-06〜 = 1.6243 等)。
  // 手動上書き可 (manualCoef=null が「DB自動に従う」)。DB に無い月/旧 duckdb は 1.5652 に fallback。
  const { data: coefData } = useDpcCoef();
  const coefDateTo = f.dateToOverride ?? diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod).dateTo;
  const autoCoef = useMemo(() => {
    const rows = coefData?.rows ?? [];
    return rows.find((r) => r.valid_from <= coefDateTo && coefDateTo <= r.valid_to)?.coef ?? null;
  }, [coefData, coefDateTo]);
  const [manualCoef, setManualCoef] = useState<number | null>(null);
  const coefficient = manualCoef ?? autoCoef ?? 1.5652;
  // 想定入院日数 default。 tensu load 後に days_II と同期 (= 「DPCⅡ以内ギリギリ」 想定)
  const [days, setDays] = useState<number>(10);

  // Task 4: store.dpcfy (= 2024/2026/null) を渡し、 マスタ改定年度別の点数を取得。
  // null (= 全期間 filter) のときは R8 (= 最新) を使う。
  const { data, isLoading, error } = useDpcTensu(dpc14, f.dpcfy);
  // 表示用版表記
  const fyLabel = f.dpcfy === 2024 ? "令和 6 年度版" : "令和 8 年度版";
  const notFound = (data && "notFound" in data) ? true : false;
  const tensu = (data && !("notFound" in data)) ? data : null;

  // 2026-05-28: 14 桁が変わるたびに days を days_II にリセット (= ユーザ指示)。
  // ユーザが slider 操作した直後でも、 別 dpc14 を選び直したら 新 days_II に戻る (= 自然挙動)。
  useEffect(() => {
    if (tensu?.days_II != null && tensu.days_II > 0) {
      setDays(tensu.days_II);
    }
  }, [tensu?.dpc14, tensu?.days_II]);

  // 上位表で何も選んでいない & 手動入力もない → placeholder
  const noSelection = !autoSelected && !manualDpc14;

  // 包括金額計算
  const calc = useMemo(() => {
    if (!tensu || tensu.hokatsu_flag !== "包括") return null;
    if (tensu.days_I == null || tensu.days_II == null || tensu.days_III == null ||
        tensu.points_I == null || tensu.points_II == null || tensu.points_III == null) return null;
    // 期間別に日数 × 点数 を積算
    const cap1 = Math.min(days, tensu.days_I);
    const cap2 = Math.min(days, tensu.days_II) - tensu.days_I;
    const cap3 = Math.min(days, tensu.days_III) - tensu.days_II;
    const d1 = Math.max(0, cap1);
    const d2 = Math.max(0, cap2);
    const d3 = Math.max(0, cap3);
    const dOver = Math.max(0, days - tensu.days_III);
    const points1 = d1 * tensu.points_I;
    const points2 = d2 * tensu.points_II;
    const points3 = d3 * tensu.points_III;
    const points  = points1 + points2 + points3;
    // 1 点 = 10 円、 medical coefficient で補正
    const yen = points * coefficient * 10;
    return { d1, d2, d3, dOver, points1, points2, points3, points, yen };
  }, [tensu, days, coefficient]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-md border border-slate-200 p-4">
        <h2 className="text-base font-semibold mb-1">📈 DPC 包括金額 (1 日あたり) — 厚労省電子点数表 {fyLabel}</h2>
        <p className="text-xs text-slate-500 mb-3">
          上の表で疾患 / 手術 / 処置 を選択すると、 該当 14 桁の包括点数 (入院期間 I/II/III 階段) と 包括金額試算 を表示します。
          手動で 14 桁を上書き入力することも可能 (= 任意の DPC14 を試算)。
        </p>

        {/* 入力部 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* 14 桁直接入力 (auto-sync + manual override) */}
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              DPC 14 桁コード
              {autoSelected && manualDpc14 == null && (
                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-800">
                  ↑ 上の表から自動取得
                </span>
              )}
              {manualDpc14 != null && (
                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">
                  手動入力中
                </span>
              )}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={dpc14}
                onChange={(e) => setManualDpc14(e.target.value.trim().toLowerCase())}
                maxLength={14}
                className="flex-1 px-2 py-1 text-sm font-mono border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300"
                placeholder="上の表で選択 または 010060xx9914xx"
              />
              {manualDpc14 != null && (
                <button
                  type="button"
                  onClick={() => setManualDpc14(null)}
                  className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50"
                  title="手動入力をクリアして 上の表の選択に戻す"
                >
                  ↑ 表に戻す
                </button>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              「x」 はワイルドカード。 14 桁未満 / 形式不正は無視。 マッチがなければ 「該当なし」。
            </p>
          </div>

          {/* 現在の連動 source 表示 */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              連動元
            </label>
            <div className="text-xs font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 leading-relaxed">
              {f.selectedDpc14 ? <>DPC14: {f.selectedDpc14}</> :
               f.selectedDpc12 ? <>DPC12: {f.selectedDpc12} + xx</> :
               f.selectedDpc11 ? <>DPC11: {f.selectedDpc11} + xxx</> :
               f.selectedDpc10 ? <>DPC10: {f.selectedDpc10} + xxxx</> :
               f.dpc6          ? <>DPC6:  {f.dpc6} + xxxxxxxx</> :
                                 <span className="text-slate-400">未選択</span>}
            </div>
          </div>
        </div>

      </div>

      {/* 結果表示 */}
      {noSelection && (
        <div className="text-sm text-slate-600 bg-sky-50 border border-sky-200 rounded p-4 text-center">
          <div className="font-semibold text-sky-800">↑ 上の表で疾患 / 手術 / 処置 を選択してください</div>
          <div className="text-xs text-sky-700 mt-1">
            DPC6 (表 1) → DPC10 → DPC11 → DPC12 → DPC14 のいずれかを選ぶと、 該当 14 桁の包括点数を表示します。
            または上のテキスト box に 14 桁コードを手入力。
          </div>
        </div>
      )}
      {!noSelection && !/^[0-9xX]{14}$/.test(dpc14) && (
        <div className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
          14 桁の DPC コードを入力してください (数字 + x ワイルドカード、 計 14 文字)。
        </div>
      )}
      {isLoading && (
        <div className="text-sm text-slate-500">読み込み中…</div>
      )}
      {error && (
        <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-3">
          取得エラー: {(error as Error).message}
        </div>
      )}
      {notFound && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          マスタに該当する診断群分類がありません (= dpc14 = {dpc14})。
          ワイルドカード x を入れて検索範囲を広げてみてください。
        </div>
      )}

      {tensu && tensu.hokatsu_flag === "包括" && tensu.days_I != null && tensu.points_I != null &&
       tensu.days_II != null && tensu.points_II != null &&
       tensu.days_III != null && tensu.points_III != null && (
        <>
          {/* 階段チャート */}
          <div className="bg-white rounded-md border border-slate-200 p-3">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              DPC包括金額 (1 日あたり) — <span className="font-mono text-emerald-700">{tensu.matched_code}</span>
            </div>
            <DpcStepChart
              days_I={tensu.days_I}
              days_II={tensu.days_II}
              days_III={tensu.days_III}
              points_I={tensu.points_I}
              points_II={tensu.points_II}
              points_III={tensu.points_III}
              days={days}
              coefficient={coefficient}
            />
          </div>

          {/* 期間表 + 計算結果 */}
          <div className="bg-white rounded-md border border-slate-200 p-3">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              入院期間別点数 + 想定 {days} 日の包括金額
              <span className="ml-2 text-xs text-slate-500">
                (= 1 日点数 × 期間内日数 × 係数 {coefficient.toFixed(3)} × 10 円)
              </span>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50">
                <tr className="text-xs text-slate-600">
                  <th className="text-left  px-1.5 py-0 border-b border-slate-200 text-[11px]">期間</th>
                  <th className="text-right px-1.5 py-0 border-b border-slate-200 text-[11px]">境界日</th>
                  <th className="text-right px-1.5 py-0 border-b border-slate-200 text-[11px]">1 日点数</th>
                  <th className="text-right px-1.5 py-0 border-b border-slate-200 text-[11px]">期間内日数</th>
                  <th className="text-right px-1.5 py-0 border-b border-slate-200 text-[11px]">期間内点数小計</th>
                  <th className="text-right px-1.5 py-0 border-b border-slate-200 text-[11px]">期間内金額</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                <tr>
                  <td className="px-1.5 py-0 border-b border-slate-100 text-[11px] font-sans">
                    <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#10b981" }} />
                    期間 I
                  </td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">〜 {tensu.days_I} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{tensu.points_I.toLocaleString()} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.d1 : 0} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.points1.toLocaleString() : 0} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? Math.round(calc.points1 * coefficient * 10).toLocaleString() : 0} 円</td>
                </tr>
                <tr>
                  <td className="px-1.5 py-0 border-b border-slate-100 text-[11px] font-sans">
                    <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#f59e0b" }} />
                    期間 II
                  </td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{tensu.days_I + 1} 〜 {tensu.days_II} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{tensu.points_II.toLocaleString()} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.d2 : 0} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.points2.toLocaleString() : 0} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? Math.round(calc.points2 * coefficient * 10).toLocaleString() : 0} 円</td>
                </tr>
                <tr>
                  <td className="px-1.5 py-0 border-b border-slate-100 text-[11px] font-sans">
                    <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#ef4444" }} />
                    期間 III
                  </td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{tensu.days_II + 1} 〜 {tensu.days_III} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{tensu.points_III.toLocaleString()} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.d3 : 0} 日</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? calc.points3.toLocaleString() : 0} 点</td>
                  <td className="text-right px-1.5 py-0 border-b border-slate-100 text-[11px]">{calc ? Math.round(calc.points3 * coefficient * 10).toLocaleString() : 0} 円</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-1.5 py-0 font-semibold text-[11px] font-sans" colSpan={4}>包括分 合計</td>
                  <td className="text-right px-1.5 py-0 font-semibold text-[11px]">{calc ? calc.points.toLocaleString() : 0} 点</td>
                  <td className="text-right px-1.5 py-0 font-semibold text-[11px]">{calc ? Math.round(calc.yen).toLocaleString() : 0} 円</td>
                </tr>
                {calc && calc.dOver > 0 && (
                  <tr className="bg-amber-50">
                    <td className="px-1.5 py-0 font-sans text-[11px]" colSpan={6}>
                      <span className="text-xs text-amber-700">
                        ⚠ 入院期間 III 超 ({calc.dOver} 日) は <b>出来高算定</b> (=本表の対象外、 実際の出来高合算が必要)
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tensu && tensu.hokatsu_flag === "出来高" && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm">
          <div className="font-semibold text-amber-800 mb-1">この診断群分類は <b>出来高算定</b> 対象です</div>
          <div className="text-xs text-amber-700">
            電子点数表 11) 診断群分類点数表 (= マッチコード <span className="font-mono">{tensu.matched_code}</span>)
            に 1 日あたり点数の定義がありません。 包括対象外 = 実施した出来高の合算で計算してください。
          </div>
        </div>
      )}

      {/* メタ情報表 */}
      {tensu && (
        <div className="bg-white rounded-md border border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-700 mb-2">診断群分類 詳細</div>
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1 w-28">マッチコード</th>
                <td className="font-mono p-1">{tensu.matched_code}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">傷病名</th>
                <td className="p-1">{tensu.name_shobyo ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">手術</th>
                <td className="p-1">{tensu.name_shujutsu ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">処置 1</th>
                <td className="p-1">{tensu.name_proc1 ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">処置 2</th>
                <td className="p-1">{tensu.name_proc2 ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">副傷病</th>
                <td className="p-1">{tensu.name_fukusho ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">重症度</th>
                <td className="p-1">{tensu.name_jusho ?? <span className="text-slate-400">—</span>}</td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">区分</th>
                <td className="p-1">
                  <span className={tensu.hokatsu_flag === "包括"
                    ? "inline-block px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800"
                    : "inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800"}>
                    {tensu.hokatsu_flag}
                  </span>
                </td>
              </tr>
              <tr>
                <th className="text-left text-xs text-slate-500 p-1">有効期間</th>
                <td className="p-1 text-xs text-slate-600">
                  {tensu.valid_from ?? "—"} 〜 {tensu.valid_to === "99999999" ? "(未定)" : (tensu.valid_to ?? "—")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* 入力部 (= 係数 + 想定入院日数) は最下部に配置。 ユーザ指示 2026-05-28: 上部から下げて
          階段チャート / 期間表 / メタ情報表 を先に視認できる順序にした。 */}
      <div className="bg-white rounded-md border border-slate-200 p-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              医療機関別係数
              {autoCoef != null && manualCoef == null && (
                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-800">
                  DB自動 ({ymJp(coefDateTo)} 適用)
                </span>
              )}
              {manualCoef != null && (
                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">
                  手動入力中
                </span>
              )}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={coefficient}
                onChange={(e) => setManualCoef(Math.max(0, Number(e.target.value) || 0))}
                step="0.001"
                min={0}
                className="w-32 px-2 py-1 text-sm font-mono border border-slate-300 rounded"
              />
              {manualCoef != null && (
                <button
                  type="button"
                  onClick={() => setManualCoef(null)}
                  className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50"
                  title="手動入力をクリアして DB の月次係数に戻す"
                >↺ DB値に戻す</button>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              源 = 電カル masijinyusysf (月次)。期間終了月に有効な係数を自動適用
              {autoCoef != null ? ` (= ${autoCoef})` : " (DB未取得のため 1.5652 に fallback)"}。
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              想定入院日数: <span className="font-mono">{days} 日</span>
            </label>
            <input
              type="range"
              min={1}
              max={60}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>

    </div>
  );
}
